// "Look back in time" (issue 27): per channel, what its source permits, how far back we hold posts,
// and a button that queues a history run (a feed run that also reads older pages where allowed).
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { Badge, Empty, ErrorMsg, Loading, Section, Time, Unknown } from '../../components/ui';
import { api, errorText, newIdempotencyKey } from '../../lib/api';
import type { HistoryOverview } from '../../lib/deepTypes';
import { useApi } from '../../lib/session';
import type { Job, StudioDetail } from '../../lib/types';

const DONE = ['succeeded', 'partial', 'failed', 'cancelled'];

export function HistorySection({ studio, onDone }: { studio: StudioDetail; onDone?: () => void }) {
  const h = useApi<HistoryOverview>(`/api/studios/${studio.id}/history`);
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [following, setFollowing] = useState<{ id: string; channel: string } | null>(null);
  const deep = studio.research_depth === 'deep';

  // Follow the queued job until it ends, then refresh the table.
  useEffect(() => {
    if (!following) return;
    let stop = false;
    const tick = async () => {
      try {
        const j = await api<Job>(`/api/jobs/${following.id}`);
        if (stop) return;
        if (DONE.includes(j.status)) {
          setMsg(`Looking back on ${following.channel}: ${j.status}${j.error ? ` (${j.error})` : ''}.`);
          setFollowing(null);
          h.reload();
          onDone?.();
          return;
        }
      } catch {
        /* keep polling */
      }
      if (!stop) setTimeout(tick, 3000);
    };
    const t = setTimeout(tick, 1500);
    return () => {
      stop = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [following?.id]);

  async function run(channel: string) {
    setBusy(channel);
    setErr('');
    setMsg('');
    try {
      const r = await api<{ job_id: string; created: boolean }>(`/api/studios/${studio.id}/history`, {
        body: { channel },
        idempotencyKey: newIdempotencyKey(),
      });
      setMsg(r.created ? `Looking back on ${channel} is queued.` : `A look-back on ${channel} is already queued or running; following it.`);
      setFollowing({ id: r.job_id, channel });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy('');
    }
  }

  return (
    <Section title="Look back in time">
      {!deep && (
        <p className="msg info small">
          Looking back uses a Deep studio’s channels. Promote this studio to Deep first; if “look back on promotion” is on in Settings, it starts by itself.
        </p>
      )}
      {h.loading && !h.data ? (
        <Loading what="Loading history" />
      ) : !h.data ? (
        <ErrorMsg>{h.error}</ErrorMsg>
      ) : h.data.channels.length === 0 ? (
        <Empty>No channel.</Empty>
      ) : (
        <div className="table-wrap">
          <table className="table" data-testid="history-table">
            <thead>
              <tr>
                <th>Channel</th>
                <th>What its source permits</th>
                <th>Oldest post held</th>
                <th>Last look-back</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {h.data.channels.map((c) => (
                <tr key={c.channel} data-channel={c.channel}>
                  <td>
                    {c.channel}
                    {c.watched && <span className="muted small"> · watched</span>}
                  </td>
                  <td className="small">
                    {c.permitted ? c.permits : <span className="muted">{c.permits}</span>}
                    {!c.permitted && <Badge tone="bad">not collected</Badge>}
                  </td>
                  <td className="small">{c.oldest_post ? <Time iso={c.oldest_post} /> : <Unknown>{c.posts_held === 0 ? 'no post held' : 'no dated post'}</Unknown>}</td>
                  <td className="small" data-testid="history-reach">
                    {c.reach ?? <Unknown>not run yet</Unknown>}
                    {c.last_job && (
                      <div className="muted">
                        <Link to={`/jobs/${c.last_job.id}`}>job {c.last_job.id.slice(0, 8)}</Link> · {c.last_job.status}
                        {c.last_job.finished_at && (
                          <>
                            {' '}
                            · <Time iso={c.last_job.finished_at} />
                          </>
                        )}
                      </div>
                    )}
                  </td>
                  <td>
                    {c.permitted && (
                      <button
                        type="button"
                        className="btn small"
                        disabled={!c.can_run || busy === c.channel || following?.channel === c.channel}
                        title={
                          !deep ? 'Promote the studio to Deep first' : !c.watched ? 'This channel is not watched: add it in Deep settings' : 'Read older posts where the source allows it'
                        }
                        onClick={() => void run(c.channel)}
                      >
                        {following?.channel === c.channel ? 'Looking back…' : 'Look back in time'}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {msg && <p className="msg ok small">{msg}</p>}
      <ErrorMsg>{err}</ErrorMsg>
      {h.data && <p className="muted small">{h.data.note}</p>}
    </Section>
  );
}
